import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api.js';
import { useIsMobile } from '../lib/useIsMobile.js';
import { useAuth } from '../lib/auth.jsx';
import { brandCurrency } from '../lib/brand.js';
import { currencySymbol } from '../lib/currency.js';
import Icon from './Icon.jsx';
import TileBrowser from './goals/TileBrowser.jsx';
import { GoalCard } from './goals/GoalViz.jsx';
import DashboardPicker from './DashboardPicker.jsx';
import AskOwlMetric from './AskOwlMetric.jsx';
import { localISODate, savedGoalDates, goalDatesProblem, pickerMin } from '../lib/goalDates.mjs';

// Set or edit an event goal (the Results pillar). Two ways to track it, per the
// spec: a LIVE number off a dashboard tile (the tile you look at becomes the goal
// you track — zero query-building), or MANUAL (you enter the number — the
// universal fallback for sponsorship, cash floats, anything not yet on a tile).
// Dual-surface: identical for a client self-serving and an admin acting on their
// behalf — the server guard decides who may write. `entityId` scopes the tile
// catalogue; `suiteId` is the event the goal belongs to.
// Suggested operational areas for the goal tag (free text still allowed). Drives the
// one-row-per-tag grouping on the Goals page.
const GOAL_TAGS = ['Ticketing', 'Cashless', 'Access control', 'Audience', 'Marketing', 'Revenue', 'F&B / Bar', 'Sponsorship', 'Operations'];

export default function GoalEditor({ entityId, suiteId, suites = [], goal, scope = 'event', eventGoals = [], initialTemplate = null, prefillName = '', onClose, onSaved }) {
  const isMobile = useIsMobile();
  const { isAdmin } = useAuth();
  const editing = !!goal;
  const isPersonal = (goal ? goal.scope : scope) === 'personal';
  const hasTile = !!(goal?.metricRef?.tileId);
  const hasMetric = !!(goal?.metricRef?.measure);
  const [activeSuite, setActiveSuite] = useState(goal?.suiteId || suiteId);
  const [name, setName] = useState(goal?.name || prefillName || ''); // prefillName: an Actions-hub draft rides in
  const [track, setTrack] = useState(hasMetric ? 'metric' : hasTile ? 'tile' : 'manual'); // 'metric' | 'tile' | 'manual'
  // Built-metric track (same machinery as alert rules — explore + filters + measure).
  const [gExCat, setGExCat] = useState(null); // { explores }
  const [gExKey, setGExKey] = useState(hasMetric ? `${goal.metricRef.model}::${goal.metricRef.view}` : '');
  const [gMeasure, setGMeasure] = useState(goal?.metricRef?.measure || '');
  const [gFilters, setGFilters] = useState(() => Object.entries(goal?.metricRef?.metricFilters || {}).map(([field, value]) => ({ field, value })));
  const [gVals, setGVals] = useState({}); // field -> [values] | 'loading'
  const [dashboardId, setDashboardId] = useState(goal?.metricRef?.dashboardId || '');
  const [tileId, setTileId] = useState(goal?.metricRef?.tileId || '');
  const [target, setTarget] = useState(goal ? String(goal.targetValue ?? '') : '');
  const [targetMax, setTargetMax] = useState(goal?.targetMax != null ? String(goal.targetMax) : ''); // upper bound for 'range' goals
  const [parts, setParts] = useState(goal?.parts || []); // composition slices [{label, target, focus, ref?}]
  const [partTol, setPartTol] = useState(goal?.parts?.[0]?.tol != null ? String(goal.parts[0].tol) : '5'); // ±pp band
  const [partsLoading, setPartsLoading] = useState(false);
  const [compMode, setCompMode] = useState(goal?.parts?.some((p) => p && p.ref && p.ref.tileId) ? 'tiles' : 'breakdown'); // breakdown tile | a tile per slice
  const [unit, setUnit] = useState(goal?.unit || 'tickets');
  const [tag, setTag] = useState(goal?.tag || ''); // operational area — groups goals into rows on the Goals page
  const [customCats, setCustomCats] = useState([]); // the client's own categories (shared with alerts)
  const [direction, setDirection] = useState(goal?.direction || 'at_least');
  const isComp = direction === 'composition';
  const [byDate, setByDate] = useState(goal?.byDate ? goal.byDate.slice(0, 10) : '');
  const [startDate, setStartDate] = useState(goal?.startDate ? goal.startDate.slice(0, 10) : ''); // sell-window start (pace anchor)
  const [northStar, setNorthStar] = useState(!!goal?.isNorthStar);
  const [current, setCurrent] = useState(''); // manual goals: enter today's actual
  const [display, setDisplay] = useState(goal?.display || 'bar'); // bar | ring | dial
  // Baseline — "recreate last event": start the goal from a comparable past
  // event's actual, so the target isn't a blank guess and the goal shows
  // "vs last time". Tile goals read the SAME tile under that event's scope;
  // manual goals enter the number by hand.
  const [baselineSuiteId, setBaselineSuiteId] = useState(goal?.baselineEventId || '');
  const [baselineValue, setBaselineValue] = useState(goal?.baselineValue != null ? String(goal.baselineValue) : '');
  const [baselineLoading, setBaselineLoading] = useState(false);
  // How "last time" is sourced: a past event (reuse the tracking tile under its scope),
  // a dashboard tile you pick (read live now), or a number you type. Stored as a value.
  const [baselineMode, setBaselineMode] = useState(goal?.baselineRef?.tileId ? 'tile' : (goal?.baselineEventId ? 'event' : 'manual')); // 'manual' | 'event' | 'tile'
  const [baselineDashboardId, setBaselineDashboardId] = useState(goal?.baselineRef?.dashboardId || '');
  const [baselineTileId, setBaselineTileId] = useState(goal?.baselineRef?.tileId || '');
  const [pastSuites, setPastSuites] = useState([]); // other events to compare against
  // Milestones — weekly/monthly checkpoints on the way to the target (Slice C).
  const [milestones, setMilestones] = useState(goal?.milestones || []);
  // Which form sections are expanded. The form asks for a lot, so it opens as a
  // short list of closed sections rather than a wall of fields — you work down
  // it one at a time. A NEW goal starts on the basics; EDITING opens nothing, so
  // you land on a summary of the whole goal and open only what you came to change.
  // Basic vs advanced. Most goals are "track this tile, hit this number" — the
  // metric builder, the checkpoint-curve suggester and the mix/split goal type
  // are power tools that made the form look far harder than the common case.
  // They're hidden behind one switch. A goal that ALREADY uses one opens with it
  // on, so editing never hides the thing you came to change.
  const usesAdvanced = !!(goal && (goal.metricRef?.measure || goal.curveRef?.tileId || goal.curveRef?.seriesId || goal.direction === 'composition'));
  const [advanced, setAdvanced] = useState(usesAdvanced);
  // Switching back to Basic must not strand the form on a control it just hid:
  // a half-built metric falls back to the dashboard picker, a mix/split goal
  // back to "hit a target".
  const setMode = (adv) => {
    setAdvanced(adv);
    if (adv) return;
    if (track === 'metric') setTrack('tile');
    if (direction === 'composition') setDirection('at_least');
  };
  const [openSec, setOpenSec] = useState(() => (goal ? {} : { basics: true }));
  const toggleSec = (id) => setOpenSec((o) => ({ ...o, [id]: !o[id] }));
  const openOnly = (id) => setOpenSec((o) => ({ ...o, [id]: true }));
  // Checkpoint suggester — link a tile that holds the value over time (a sell-by-date
  // curve), read its shape under a comparable past event, and suggest checkpoints
  // scaled to this goal's target ("last year's sell-by shape → weekly checkpoints").
  const [curveOpen, setCurveOpen] = useState(!!(goal?.curveRef?.tileId || goal?.curveRef?.seriesId)); // reopen showing the saved link (tile or upload)
  const [curveDashboardId, setCurveDashboardId] = useState(goal?.curveRef?.dashboardId || '');
  const [curveTileId, setCurveTileId] = useState(goal?.curveRef?.tileId || '');
  const [curveCadence, setCurveCadence] = useState(goal?.curveRef?.cadence || 'monthly'); // 'weekly' | 'monthly'
  const [compareKey, setCompareKey] = useState(goal?.curveRef?.compareKey || ''); // '' = last year (auto)
  const [thisKey, setThisKey] = useState(goal?.curveRef?.thisKey || ''); // '' = auto (Current Event lock / newest)
  // Uploaded "last time" curve — for an event with no history tile in Looker, a
  // spreadsheet (point, value) plays the same role: pace line, vs-last-time and
  // checkpoint suggestions all read it. '' = use a dashboard tile instead.
  const [curveSeriesId, setCurveSeriesId] = useState(goal?.curveRef?.seriesId || '');
  const [seriesList, setSeriesList] = useState(null); // [{id,name,points,from,to,total}] | null while loading
  const [seriesBusy, setSeriesBusy] = useState(false);
  const [seriesErr, setSeriesErr] = useState('');
  const fileRef = useRef(null);
  const [curveYears, setCurveYears] = useState([]); // prior-period keys to compare against
  const [curveAllKeys, setCurveAllKeys] = useState([]); // every pivot key (choices for "this event")
  const [detectedThisKey, setDetectedThisKey] = useState(''); // what auto currently resolves to
  const [curveSeries, setCurveSeries] = useState(null); // { loading } | [{ t, v }]
  const [suggInfo, setSuggInfo] = useState(null); // server-computed { checkpoints:[{byDate,fraction,lastValue}] }
  // Personal-goal fields (Slice D): who can see it + which event goal it feeds.
  const [visibility, setVisibility] = useState(goal?.visibility || 'team');
  const [rollsUpTo, setRollsUpTo] = useState(goal?.rollsUpTo || '');
  const [cat, setCat] = useState(null);       // tile catalogue { dashboards: [...] }
  const [preview, setPreview] = useState(null); // live value of the picked tile
  const [templates, setTemplates] = useState([]); // reusable goal templates for this entity
  const [tmplBusy, setTmplBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);

  // Load the client's dashboards/tiles for tile-tracking, the curve suggester, OR the
  // Lock the page behind the sheet + close on Escape (mirrors TileAlertModal).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [onClose]);

  // "compare to last time" tile picker.
  useEffect(() => {
    if ((track !== 'tile' && !curveOpen && baselineMode !== 'tile' && !isComp) || cat || !entityId) return;
    api.getMyDigestTiles(entityId).then(setCat).catch(() => setCat({ dashboards: [] }));
  }, [track, curveOpen, baselineMode, isComp, cat, entityId]);

  // Composition: read the chosen breakdown tile's segments and seed each part's target
  // with its current share (so you just adjust). Only auto-fills when parts are empty.
  const loadSegments = async () => {
    if (!dashboardId || !tileId || !activeSuite) return;
    setPartsLoading(true);
    try {
      const r = await api.goalTileSeries(activeSuite, dashboardId, tileId);
      const rows = (r.series || []).filter((x) => x && Number.isFinite(Number(x.v)));
      const total = rows.reduce((s, x) => s + Number(x.v), 0) || 1;
      setParts(rows.map((x) => ({ label: String(x.t), target: Math.round((Number(x.v) / total) * 100) })));
    } catch { /* ignore */ } finally { setPartsLoading(false); }
  };
  useEffect(() => { if (isComp && compMode === 'breakdown' && dashboardId && tileId && parts.length === 0) loadSegments(); }, [isComp, compMode, dashboardId, tileId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reusable goal templates for this client.
  useEffect(() => { if (entityId) api.goalTemplates(entityId).then((r) => setTemplates(r.templates || [])).catch(() => {}); }, [entityId]);
  // The client's own categories (shared with alerts) — merged with the presets below.
  useEffect(() => { if (entityId) api.categories(entityId).then((r) => setCustomCats(r.categories || [])).catch(() => {}); }, [entityId]);
  const allTags = [...new Set([...GOAL_TAGS, ...customCats])];
  const tagIsNew = !!tag.trim() && !allTags.some((c) => c.toLowerCase() === tag.trim().toLowerCase());
  const saveCategory = async () => {
    const name = tag.trim(); if (!name || !entityId) return;
    try { const r = await api.addCategory(entityId, name); setCustomCats(r.categories || []); } catch { /* ignore */ }
  };

  // A tile ref carries the dashboard NAME + tile title (the key components) so a
  // template — especially a global one — re-resolves to THIS client's matching
  // dashboard/tile by name when the original IDs don't exist here.
  const dashList = () => (cat?.dashboards || []);
  const refWithNames = (dId, tId) => {
    const d = dashList().find((x) => x.dashboardId === dId);
    const t = d?.tiles?.find((x) => x.tileId === tId);
    return { dashboardId: dId, tileId: tId, dashboardName: d?.title || '', tileName: t?.title || '' };
  };
  const resolveRef = (ref) => {
    const ds = dashList();
    let d = ds.find((x) => x.dashboardId === ref.dashboardId) || (ref.dashboardName && ds.find((x) => (x.title || '') === ref.dashboardName));
    if (!d) return { dashboardId: '', tileId: '' };
    const t = (d.tiles || []).find((x) => x.tileId === ref.tileId) || (ref.tileName && (d.tiles || []).find((x) => (x.title || '') === ref.tileName));
    return { dashboardId: d.dashboardId, tileId: t ? t.tileId : '' };
  };
  const [pendingRefs, setPendingRefs] = useState(null); // template refs to resolve once the catalogue loads

  // ── Built-metric track helpers ─────────────────────────────────────────────────
  const gExplore = (gExCat?.explores || []).find((e) => `${e.model}::${e.view}` === gExKey) || null;
  // Load the data sources for the SELECTED event, and reload when that changes.
  // It used to fetch once and never again (`!gExCat`), so the list a first,
  // unlucky fetch produced — including an empty one — stuck for the whole
  // session no matter which event you then picked. The failure is also kept
  // (never swallowed into a bare empty list) so the editor can say WHY.
  const gExCatFor = useRef('');
  useEffect(() => {
    if (track !== 'metric' || !activeSuite || gExCatFor.current === activeSuite) return;
    const changed = !!gExCatFor.current;
    gExCatFor.current = activeSuite;
    setGExCat(null);
    if (changed) { setGExKey(''); setGMeasure(''); setGFilters([]); } // a different event can expose different sources
    // The catalogue is CLIENT-wide (it's built from the client's dashboards), so a
    // single event returning nothing — no dashboards linked to it, or a suite this
    // login can't read — shouldn't leave the picker empty when a sibling event of
    // the same client can serve the very same list. Ask that event, then fall back
    // to the others in turn; the diagnostics of the FIRST answer are kept, so an
    // empty result still explains itself.
    (async () => {
      const tried = [activeSuite, ...(suites || []).map((x) => x.id).filter((id) => id && id !== activeSuite)].slice(0, 4);
      let first = null;
      for (const sid of tried) {
        let r;
        try { r = await api.goalMetricCatalog(sid); }
        catch (e) { r = { explores: [], error: String(e.message || e).slice(0, 300) }; }
        if (!first) first = r;
        if ((r.explores || []).length) { setGExCat(sid === activeSuite ? r : { ...r, viaSuiteId: sid }); return; }
      }
      setGExCat(first || { explores: [] });
    })();
  }, [track, activeSuite]); // eslint-disable-line react-hooks/exhaustive-deps
  const gLoadVals = (field, opts = {}) => {
    if (!field || !gExplore) return;
    if (!opts.allEvents && !gEventSettled) return; // the event lands first; the effect below re-runs
    const key = opts.allEvents ? field : gValKey(field);
    const cur = gVals[key];
    if (cur === 'loading' || (Array.isArray(cur) && cur.length)) return; // empty/failed stays retryable
    setGVals((m) => ({ ...m, [key]: 'loading' }));
    // allEvents: the Event picker lists EVERY event of this client (organiser scope),
    // not just the one this suite is pinned to — so the goal can name any event.
    // Every OTHER dropdown rides the event already chosen: it narrows the values to
    // that event, and it's what lets them resolve with no dashboard tile on this explore.
    const scopeFilters = (!opts.allEvents && gEventField && gEventValue) ? { [gEventField]: gEventValue } : undefined;
    api.goalMetricFilterValues(activeSuite, { model: gExplore.model, view: gExplore.view, field, ...(opts.allEvents ? { allEvents: true } : {}), ...(scopeFilters ? { filters: scopeFilters } : {}) })
      .then((r) => setGVals((m) => ({ ...m, [key]: r.values || [] })))
      .catch(() => setGVals((m) => ({ ...m, [key]: [] })));
  };
  // The event-name dimension for this explore — so the goal is pinned to ONE event and
  // its number can never span others (mirrors the alert editor). Prefer the canonical
  // core_events.name, then an explicit "Event"/"Event Name" label — never a cashless /
  // comparison / past-event variant, which filters the wrong join and reads 0.
  const gNotEventName = (s) => /cashless|comparison|compare|past|previous|prior|transaction/i.test(String(s || ''));
  const gDims = () => (gExplore?.dimensions || []);
  const gEventDim = gDims().find((d) => d.name === 'core_events.name')
    || gDims().find((d) => /^(current )?event( name)?$/i.test(String(d.label).trim()) && !gNotEventName(d.label) && !gNotEventName(d.name))
    || gDims().find((d) => /\bevent\b/i.test(d.label) && /name/i.test(d.label) && !gNotEventName(d.label) && !gNotEventName(d.name))
    || gDims().find((d) => /(^|[._])events?[._]name$/i.test(d.name) && !gNotEventName(d.name))
    || null;
  const gEventField = gEventDim?.name || '';
  const gEventValue = gEventField ? (gFilters.find((f) => f.field === gEventField)?.value || '') : '';
  const setGEventValue = (v) => setGFilters((fs) => { const others = fs.filter((f) => f.field !== gEventField); return v ? [{ field: gEventField, value: v }, ...others] : others; });
  // A value list belongs to ONE event — this year's Ticket Types are not last year's —
  // so the cache is keyed by the event in force, not by the field alone (mirrors the
  // alert editor). Keyed by field only, the first list fetched (often before the event
  // was pinned, i.e. every event's values) stuck for the session and changing the
  // Event never refreshed it. The Event picker itself is client-wide → keys on its name.
  const gValKey = (field, ev = gEventValue) => (field === gEventField ? field : `${field}@@${ev || '*'}`);
  const gValsOf = (field) => gVals[gValKey(field)];
  const gEventSettled = !gEventField || !!gEventValue || Array.isArray(gVals[gEventField]);
  // Re-runs when the EVENT changes (or first lands), so every value dropdown is
  // refetched under the event now in force instead of the previous event's.
  useEffect(() => { if (track === 'metric' && gExplore) for (const f of gFilters) if (f.field) gLoadVals(f.field); }, [track, gExKey, gExplore, gFilters.length, gEventValue, gEventSettled]); // eslint-disable-line react-hooks/exhaustive-deps
  const gSuiteName = (suites.find((x) => x.id === activeSuite) || {}).name || '';
  // Ask the Owl to build the metric (shared with the alert editor) — it fills these
  // pickers in; the person still reviews and saves. Goals can also pace against a past
  // event, so its compare suggestion becomes a one-tap curve build here.
  const fillFromOwl = (m) => {
    setGExKey(`${m.model}::${m.view}`);
    setGMeasure(m.measure || '');
    setGFilters([...(m.event ? [{ field: m.event.field, value: m.event.value }] : []), ...(m.filters || []).map((f) => ({ field: f.field, value: f.value }))]);
    // Seed the value dropdowns from what the server already looked up, so the filter
    // rows land as SELECTS (with the real options) rather than free-text boxes.
    setGVals((v) => { const next = { ...v }; for (const f of (m.filters || [])) if (Array.isArray(f.options) && f.options.length) next[gValKey(f.field, m.event?.value || '')] = f.options; return next; });
    setOwlCurve({});
  };
  const [owlCurve, setOwlCurve] = useState({}); // { busy?, done?, err? } — the "pace it against X" build
  const buildOwlCurve = async (eventName) => {
    if (!entityId || owlCurve.busy) return;
    setOwlCurve({ busy: true });
    try {
      const filtersObj = {}; for (const f of gFilters) if (f.field && f.value) filtersObj[f.field] = f.value;
      const r = await api.goalSeriesFromEvent(entityId, { eventName, measure: gMeasure, filters: filtersObj, name: `${eventName} (computed)` });
      setSeriesList(null); setCurveSeriesId(r.id); setCurveOpen(true);
      setOwlCurve({ done: `${r.name} · ${r.points} days of sales` });
    } catch (e) { setOwlCurve({ err: (e && e.message) || 'Couldn’t compute that curve.' }); }
  };
  useEffect(() => { if (track === 'metric' && gExplore && gEventField) gLoadVals(gEventField, { allEvents: true }); }, [track, gExKey, gEventField]); // eslint-disable-line react-hooks/exhaustive-deps
  // Default-pin to THIS event when its name is among the values, so a new built metric
  // is correctly scoped out of the box (and still changeable).
  useEffect(() => {
    if (track !== 'metric' || !gEventField || gEventValue) return;
    const vals = gVals[gEventField];
    if (!Array.isArray(vals) || !vals.length) return;
    const norm = (s) => String(s).trim().toLowerCase();
    const match = gSuiteName && vals.find((v) => norm(v) === norm(gSuiteName) || norm(v).includes(norm(gSuiteName)) || norm(gSuiteName).includes(norm(v)));
    if (match) setGEventValue(match);
  }, [gVals, gEventField, gEventValue, gSuiteName, track]); // eslint-disable-line react-hooks/exhaustive-deps
  const gFiltersObj = () => { const o = {}; for (const f of gFilters) if (f.field && f.value) o[f.field] = f.value; return o; };
  const gMetricRef = () => (gExplore && gMeasure ? {
    model: gExplore.model, view: gExplore.view, measure: gMeasure,
    measureLabel: (gExplore.measures || []).find((m) => m.name === gMeasure)?.label || '',
    metricFilters: gFiltersObj(),
    metricLabel: [(gExplore.measures || []).find((m) => m.name === gMeasure)?.label || gMeasure, ...gFilters.filter((f) => f.field && f.value).map((f) => `${f.field.split('.').pop()} = ${f.value}`)].join(' · '),
  } : null);
  const [gPreview, setGPreview] = useState(null);
  useEffect(() => {
    if (track !== 'metric' || !gMeasure || !activeSuite) { setGPreview(null); return undefined; }
    let alive = true;
    // Opening a SAVED metric goal sets model/view/measure straight away, but the explore
    // catalogue lands a moment later — so hold on "reading…" until it does (`gExCat` is
    // in the deps, so this re-runs then) instead of showing "couldn't read it right now"
    // until the measure is re-picked by hand.
    if (!gExplore) { setGPreview(gExCat ? { value: null } : { loading: true }); return undefined; }
    setGPreview({ loading: true });
    const t = setTimeout(() => {
      api.goalMetricValue(activeSuite, { model: gExplore.model, view: gExplore.view, measure: gMeasure, filters: gFiltersObj() })
        .then((r) => { if (alive) setGPreview({ value: r.value }); })
        .catch(() => { if (alive) setGPreview({ value: null }); });
    }, 500);
    return () => { alive = false; clearTimeout(t); };
  }, [track, gExKey, gMeasure, activeSuite, gExCat, JSON.stringify(gFilters)]); // eslint-disable-line react-hooks/exhaustive-deps

  // The reusable subset of the current form (no dates / North Star / snapshot). Each
  // tile ref keeps the dashboard name + tile title alongside the IDs.
  const templatePayload = () => ({
    name, source: track === 'manual' ? 'manual' : 'ticketing',
    metricRef: track === 'metric' ? gMetricRef() : (track === 'tile' && dashboardId && tileId ? refWithNames(dashboardId, tileId) : null),
    targetValue: Number(target) || 0, targetMax: direction === 'range' && targetMax !== '' ? Number(targetMax) : null, unit, tag, direction, display,
    curveRef: curveSeriesId ? { seriesId: curveSeriesId, cadence: curveCadence } : (curveDashboardId && curveTileId) ? { ...refWithNames(curveDashboardId, curveTileId), cadence: curveCadence, ...(compareKey ? { compareKey } : {}), ...(thisKey ? { thisKey } : {}) } : null,
    baselineRef: baselineMode === 'tile' && baselineDashboardId && baselineTileId ? refWithNames(baselineDashboardId, baselineTileId) : null,
  });
  const applyTemplate = (p) => {
    if (!p) return;
    if (p.name) setName(p.name);
    if (p.targetValue != null) setTarget(String(p.targetValue));
    if (p.targetMax != null) setTargetMax(String(p.targetMax));
    if (p.direction) setDirection(p.direction);
    if (p.unit) setUnit(p.unit);
    if (p.tag) setTag(p.tag);
    if (p.display) setDisplay(p.display);
    const refs = {};
    if (p.metricRef && p.metricRef.measure) {
      setTrack('metric');
      setGExKey(`${p.metricRef.model}::${p.metricRef.view}`);
      setGMeasure(p.metricRef.measure);
      setGFilters(Object.entries(p.metricRef.metricFilters || {}).map(([field, value]) => ({ field, value })));
    } else if (p.metricRef && (p.metricRef.tileId || p.metricRef.tileName)) { setTrack('tile'); refs.metric = p.metricRef; }
    if (p.curveRef) {
      if (p.curveRef.cadence) setCurveCadence(p.curveRef.cadence);
      if (p.curveRef.compareKey) setCompareKey(p.curveRef.compareKey);
      if (p.curveRef.thisKey) setThisKey(p.curveRef.thisKey);
      if (p.curveRef.tileId || p.curveRef.tileName) { setCurveOpen(true); refs.curve = p.curveRef; }
    }
    if (p.baselineRef && (p.baselineRef.tileId || p.baselineRef.tileName)) { setBaselineMode('tile'); refs.baseline = p.baselineRef; }
    if (Object.keys(refs).length) setPendingRefs(refs); // resolved by name once the catalogue is loaded
  };
  // Opened from the Templates tab → pre-fill from the chosen template once.
  const tmplApplied = useRef(false);
  useEffect(() => { if (initialTemplate && !tmplApplied.current) { tmplApplied.current = true; applyTemplate(initialTemplate); } }, [initialTemplate]);  

  // Resolve queued template refs against this client's catalogue (by id, else by name).
  useEffect(() => {
    if (!pendingRefs || !cat?.dashboards) return;
    if (pendingRefs.metric) { const r = resolveRef(pendingRefs.metric); setDashboardId(r.dashboardId); setTileId(r.tileId); }
    if (pendingRefs.curve) { const r = resolveRef(pendingRefs.curve); setCurveDashboardId(r.dashboardId); setCurveTileId(r.tileId); }
    if (pendingRefs.baseline) { const r = resolveRef(pendingRefs.baseline); setBaselineDashboardId(r.dashboardId); setBaselineTileId(r.tileId); }
    setPendingRefs(null);
  }, [pendingRefs, cat]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveAsTemplate = async () => {
    const nm = (name || '').trim() || window.prompt('Template name?') || '';
    if (!nm.trim() || !entityId) return;
    // Admins can publish a portable scaffold to every client.
    const global = isAdmin && window.confirm('Make this available to ALL clients?\n\nOK = global template (a portable scaffold — each client links their own tiles)\nCancel = just this client');
    setTmplBusy(true);
    try {
      const r = await api.saveGoalTemplate({ entityId, name: nm.trim(), payload: { ...templatePayload(), name: nm.trim() }, global });
      if (r?.template) { setTemplates((t) => [r.template, ...t.filter((x) => x.id !== r.template.id)]); window.alert(`Saved “${r.template.name}”${r.template.global ? ' as a GLOBAL template (all clients)' : ''}.`); }
    } catch (e) { window.alert(`Couldn't save template: ${e.message}`); }
    finally { setTmplBusy(false); }
  };
  const removeTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api.deleteGoalTemplate(id); setTemplates((t) => t.filter((x) => x.id !== id)); } catch { /* ignore */ }
  };

  // Read "last time's curve" AND its checkpoint suggestions in one server call — the
  // server uses the SAME days-before alignment as the live pace engine, returning the
  // shape (for the sparkline) plus per-checkpoint fractions (target-independent, so the
  // target field below doesn't re-query). Scope is enforced server-side. Re-runs when
  // the tile, cadence or the start/deadline change (NOT on every target keystroke).
  const curveSuiteId = (baselineMode === 'event' && baselineSuiteId) ? baselineSuiteId : activeSuite;
  useEffect(() => {
    if (!curveOpen || !curveSuiteId || (!curveSeriesId && (!curveDashboardId || !curveTileId))) { setCurveSeries(null); setSuggInfo(null); return undefined; }
    let alive = true; setCurveSeries({ loading: true });
    api.goalCheckpointSuggestions(curveSuiteId, curveSeriesId
      ? { seriesId: curveSeriesId, cadence: curveCadence, startDate, byDate }
      : { dashboardId: curveDashboardId, tileId: curveTileId, cadence: curveCadence, startDate, byDate, compareKey, thisKey })
      .then((r) => { if (!alive) return; setCurveSeries(Array.isArray(r.series) ? r.series : []); setSuggInfo({ checkpoints: Array.isArray(r.checkpoints) ? r.checkpoints : [] }); setCurveYears(Array.isArray(r.years) ? r.years : []); setCurveAllKeys(Array.isArray(r.allKeys) ? r.allKeys : []); setDetectedThisKey(r.thisKey || ''); })
      .catch(() => { if (alive) { setCurveSeries([]); setSuggInfo({ checkpoints: [] }); } });
    return () => { alive = false; };
  }, [curveOpen, curveDashboardId, curveTileId, curveSuiteId, curveCadence, startDate, byDate, compareKey, thisKey, curveSeriesId]);

  // The client's saved uploads (reusable across goals), loaded when the curve
  // section is opened. A 403 = the flag is off for this client → no upload option.
  useEffect(() => { if (curveOpen && seriesList === null && entityId) api.goalSeriesList(entityId).then((r) => setSeriesList(r.series || [])).catch(() => setSeriesList([])); }, [curveOpen, seriesList, entityId]);

  // Read a .csv/.xlsx/.xls into [[point, value], …] in the browser (the same xlsx
  // reader campaign audiences use), then store it as a named curve.
  async function uploadCurve(file) {
    if (!file || seriesBusy) return;
    setSeriesBusy(true); setSeriesErr('');
    try {
      const XLSX = (await import('xlsx')).default || (await import('xlsx'));
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, blankrows: false });
      const rows = (grid || []).map((r) => [r[0], r[1]]);
      if (rows.length < 2) throw new Error('That sheet needs at least two rows of “point, value”.');
      const out = await api.goalSeriesCreate(entityId, { name: file.name.replace(/\.[^.]+$/, '').slice(0, 80), rows });
      setSeriesList((l) => [out, ...(l || [])]);
      setCurveSeriesId(out.id);
      setCurveDashboardId(''); setCurveTileId(''); // an uploaded curve replaces the tile link
    } catch (e) { setSeriesErr(e.message || 'Could not read that file.'); }
    finally { setSeriesBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  // Live value of the chosen tile, so the target is set against the real number.
  // The draft CURVE rides along: a curve-linked goal takes its current value from
  // the curve tile's this-year column, so previewing the KPI tile alone showed a
  // number the goal would abandon the moment it was saved (2,930 → 3,153). The
  // preview now resolves exactly as the saved goal does, and reports both readings.
  const draftCurveRef = curveSeriesId
    ? { seriesId: curveSeriesId, cadence: curveCadence }
    : (curveDashboardId && curveTileId ? { dashboardId: curveDashboardId, tileId: curveTileId, cadence: curveCadence, ...(compareKey ? { compareKey } : {}), ...(thisKey ? { thisKey } : {}) } : null);
  const curveRefKey = JSON.stringify(draftCurveRef);
  useEffect(() => {
    if (track !== 'tile' || !dashboardId || !tileId || !activeSuite) { setPreview(null); return undefined; }
    let alive = true; setPreview({ loading: true });
    api.goalTileValue(activeSuite, dashboardId, tileId, draftCurveRef)
      .then((r) => { if (alive) setPreview({ value: r.value, source: r.source, tileValue: r.tileValue, curveValue: r.curveValue, divergence: r.divergence }); })
      .catch(() => { if (alive) setPreview({ value: null }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track, dashboardId, tileId, activeSuite, curveRefKey]);

  // Candidate events to baseline against. Scoped STRICTLY to this profile's entity
  // — never other clients' events. Derive the entity from the CURRENT suite (not
  // the activeEntityId prop, which can be blank); if we can't resolve an entity,
  // fail closed to just the current event. The current event is included on
  // purpose: a recurring/annual event is its own "last time" (read the same tile
  // under its earlier data). The resolver still enforces per-event scope.
  useEffect(() => {
    api.mySuites().then((all) => {
      const listAll = all || [];
      const cur = listAll.find((s) => s.id === activeSuite);
      const entId = cur?.entityId || entityId;
      const scoped = entId
        ? listAll.filter((s) => s.entityId === entId)
        : listAll.filter((s) => s.id === activeSuite);
      setPastSuites(scoped);
    }).catch(() => setPastSuites([]));
  }, [entityId, activeSuite]);

  // Tile-sourced: read the SAME tile under the chosen past event's scope — that's
  // "last time's" number, with zero extra query-building (the resolver enforces
  // scope per event). Manual goals enter the baseline by hand below.
  useEffect(() => {
    if (track !== 'tile' || !baselineSuiteId || !dashboardId || !tileId) return undefined;
    let alive = true; setBaselineLoading(true);
    api.goalTileValue(baselineSuiteId, dashboardId, tileId)
      .then((r) => { if (alive) setBaselineValue(r.value == null ? '' : String(r.value)); })
      .catch(() => { if (alive) setBaselineValue(''); })
      .finally(() => { if (alive) setBaselineLoading(false); });
    return () => { alive = false; };
  }, [track, baselineSuiteId, dashboardId, tileId]);

  // Baseline from a PICKED tile — read it live (under this event's scope) and snapshot
  // the number into baselineValue, so you can compare to any tile (e.g. a last-year KPI),
  // not just the tracking tile under a past event.
  useEffect(() => {
    if (baselineMode !== 'tile' || !baselineDashboardId || !baselineTileId || !activeSuite) return undefined;
    let alive = true; setBaselineLoading(true);
    api.goalTileValue(activeSuite, baselineDashboardId, baselineTileId)
      .then((r) => { if (alive) setBaselineValue(r.value == null ? '' : String(r.value)); })
      .catch(() => { if (alive) setBaselineValue(''); })
      .finally(() => { if (alive) setBaselineLoading(false); });
    return () => { alive = false; };
  }, [baselineMode, baselineDashboardId, baselineTileId, activeSuite]);

  const dashboards = cat?.dashboards || [];
  // A goal tracks ONE headline number, so only single-value (KPI) tiles qualify —
  // a chart/table tile has no single "the number" (and the resolver would read its
  // first row, e.g. an early 0). Mirrors TileFrame's metric-tile test.
  const isKpi = (t) => { const v = t.visType || ''; return v === 'single_value' || v === 'single_value_period_over_period' || v.includes('bar_gauge'); };
  const tilesFor = (dId) => (dashboards.find((d) => d.dashboardId === dId)?.tiles || []).filter(isKpi);
  // Curve candidates are the opposite: charts/tables that carry a value-over-time
  // series (not single-value KPIs), so there's a shape to read.
  const seriesTilesFor = (dId) => (dashboards.find((d) => d.dashboardId === dId)?.tiles || []).filter((t) => !isKpi(t));
  // Only offer dashboards that actually carry a usable tile for THIS picker — a
  // KPI (single number) for a metric/baseline, or a chart/table series for a
  // breakdown/curve — so you never pick a dashboard with nothing to read. The
  // currently-selected dashboard is always kept (editing an older goal).
  // …and narrow further by the goal's CATEGORY. A ticketing goal has no business
  // scrolling past the cashless and GA4 boards, and the catalogue already groups
  // dashboards by report set — "Ticketing", "Cashless", "Finance" — which is the
  // same vocabulary the category field uses. Matching is loose (either name
  // containing the other, word by word) because a set called "Ticketing 27" and a
  // category called "Ticketing" are plainly the same thing.
  //
  // It NARROWS, never hides: if the category matches nothing we show everything
  // rather than an empty picker, and `showAllDash` is the always-present way out
  // when a client's naming doesn't line up with ours.
  const [showAllDash, setShowAllDash] = useState(false);
  const [browse, setBrowse] = useState(false); // pick the tile off the dashboard instead of the list
  const catWords = tag.trim().toLowerCase().split(/[^a-z0-9]+/i).filter((w) => w.length > 2);
  // Word-STEM comparison, both directions: a "Ticketing" category must match a
  // "Festival Tickets" set (ticketing/tickets share the stem "ticket") — plain
  // substring matching hid every dashboard whose set used a different word form.
  const stem = (w) => w.replace(/(ing|ers|er|es|s)$/, '');
  const matchesTag = (d) => {
    if (!catWords.length) return true;
    const hayWords = `${d.setName || ''} ${d.title || ''}`.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2);
    return catWords.some((w) => {
      const ws = stem(w);
      return hayWords.some((h) => { const hs = stem(h); return hs.includes(ws) || ws.includes(hs); });
    });
  };
  const dashFor = (kind, curId) => {
    const ok = kind === 'series' ? (d) => (d.tiles || []).some((t) => !isKpi(t)) : (d) => (d.tiles || []).some(isKpi);
    let base = dashboards.filter(ok);
    if (!showAllDash && catWords.length) {
      const narrowed = base.filter(matchesTag);
      if (narrowed.length) base = narrowed;
    }
    if (curId && !base.some((d) => d.dashboardId === curId)) {
      const sel = dashboards.find((d) => d.dashboardId === curId);
      if (sel) return [...base, sel];
    }
    return base;
  };
  // True only when the category is actually hiding something — so the "show all"
  // hint never appears claiming to reveal nothing.
  const narrowingBy = (kind) => {
    if (showAllDash || !catWords.length) return 0;
    const ok = kind === 'series' ? (d) => (d.tiles || []).some((t) => !isKpi(t)) : (d) => (d.tiles || []).some(isKpi);
    const all = dashboards.filter(ok);
    const narrowed = all.filter(matchesTag);
    return narrowed.length && narrowed.length < all.length ? all.length - narrowed.length : 0;
  };
  const dashNote = (kind) => {
    const hidden = narrowingBy(kind);
    if (!hidden) return null;
    return (
      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
        Showing “{tag.trim()}” dashboards · <button type="button" onClick={() => setShowAllDash(true)} style={linkBtn}>show all {hidden} others</button>
      </div>
    );
  };

  // One-line recap shown on a CLOSED section, so the setup stays reviewable
  // without reopening every section. Anything still required reads "Not set" —
  // that's how you spot what's left without a wizard's step counter.
  const tileLabel = (dId, tId) => (dashboards.find((d) => d.dashboardId === dId)?.tiles || []).find((t) => t.tileId === tId)?.title || '';
  const NOT_SET = '—';
  const secSummaries = {
    basics: [name.trim() || 'Not named yet', tag.trim(), suites.length > 1 ? suites.find((s) => s.id === activeSuite)?.name : ''].filter(Boolean).join(' · '),
    track: track === 'manual' ? 'You enter the value'
      : track === 'metric' ? (gMeasure ? 'Built metric' : NOT_SET)
        : (tileId ? tileLabel(dashboardId, tileId) || 'A dashboard tile' : NOT_SET),
    baseline: baselineValue !== '' && Number.isFinite(Number(baselineValue)) ? `Last time ${fmtNum(Number(baselineValue), unit)}` : 'Not comparing',
    target: isComp ? `Mix · ${parts.filter((p) => String(p.label).trim()).length} slices`
      : !target ? NOT_SET
        : direction === 'range' ? `${target}–${targetMax || '?'} ${unit}`
          : `${direction === 'at_most' ? 'Under ' : ''}${fmtNum(Number(target), unit)}`,
    dates: [byDate ? `by ${byDate}` : 'No deadline', milestones.length ? `${milestones.length} checkpoint${milestones.length > 1 ? 's' : ''}` : ''].filter(Boolean).join(' · '),
    look: display === 'ring' ? 'Circle' : display === 'dial' ? 'Dial' : 'Bar',
    who: isPersonal ? (visibility === 'private' ? 'Just me' : 'My team') : (northStar ? 'North Star' : 'Team goal'),
  };
  const secProps = (id) => ({ id, open: !!openSec[id], onToggle: toggleSec, summary: secSummaries[id] });

  // ── Live preview ────────────────────────────────────────────────────────────
  // The card the goal will BE, rendered from the form as it's filled in, using
  // the same GoalCard the home strip and Goals page use — so what you see while
  // building is literally what ships, bar/ring/dial and all. The current value is
  // whichever one this tracking mode has already read (no extra queries): the
  // tile preview, the built-metric preview, or the number you typed.
  const liveValue = track === 'manual'
    ? (current !== '' && !Number.isNaN(Number(current)) ? Number(current) : null)
    : track === 'metric' ? (gPreview && gPreview.value != null ? Number(gPreview.value) : null)
      : (preview && preview.value != null ? Number(preview.value) : null);
  const targetNum = target !== '' && !Number.isNaN(Number(target)) ? Number(target) : null;
  const previewGoal = {
    id: '__preview__',
    name: name.trim() || 'Your goal',
    unit,
    direction,
    display,
    targetValue: targetNum,
    targetMax: direction === 'range' && targetMax !== '' ? Number(targetMax) : null,
    byDate,
    isNorthStar: !isPersonal && northStar,
    progress: {
      value: liveValue,
      // Percent only once BOTH numbers are real — a bar filling itself in from a
      // half-typed target reads as a bug.
      pct: liveValue != null && targetNum ? Math.round((liveValue / targetNum) * 100) : null,
    },
  };
  // Worth showing once there's anything to show. Composition goals have their own
  // slice machinery that this card can't stand in for, so they don't get one.
  const showPreview = !isComp && (!!name.trim() || targetNum != null || liveValue != null);

  // Suggested checkpoints for THIS goal: the SERVER computes each checkpoint's fraction
  // of last time's total using the SAME days-before alignment as the live pace engine
  // (so the suggestions and the card's Ahead/Behind use identical math). Here we just
  // scale those fractions by the live target — keeping the target field instant (no
  // re-query) while last time's actual value at each point stays reviewable.
  function buildSuggestions() {
    const tgt = Number(target);
    const cps = suggInfo?.checkpoints || [];
    if (!cps.length || !Number.isFinite(tgt) || tgt <= 0) return [];
    return cps.map((c) => ({ byDate: c.byDate, targetValue: Math.round(tgt * c.fraction), lastValue: c.lastValue }));
  }

  // When the curve read fine but no checkpoints came out, say WHY (instead of silence).
  function suggestReason() {
    if (!Number(target) || !byDate) return 'Set a target and deadline above, then we’ll suggest checkpoints.';
    const start = startDate ? new Date(startDate) : (goal?.createdAt ? new Date(goal.createdAt) : new Date());
    const end = new Date(byDate);
    if (!(end.getTime() > start.getTime())) return 'Set a deadline in the future to space checkpoints out.';
    const days = (end.getTime() - start.getTime()) / 86400000;
    if (curveCadence === 'monthly' && days < 31) return 'Less than a month to the deadline — switch to Weekly for checkpoints.';
    if (curveCadence === 'weekly' && days < 7) return 'Less than a week to the deadline — too short for checkpoints.';
    return 'Couldn’t build checkpoints from that curve — try the other cadence.';
  }

  // Baseline UI state: do we have a usable "last time" number, are we auto-reading
  // it off a past event's tile, and did that read succeed?
  const baseFinite = baselineValue !== '' && Number.isFinite(Number(baselineValue));
  // "Auto" = the value is read for you (past event reusing the tracking tile, or a tile
  // you picked) rather than typed.
  const autoMode = (baselineMode === 'event' && !!baselineSuiteId && track === 'tile') || (baselineMode === 'tile' && !!baselineTileId);

  const updateMilestone = (i, patch) => setMilestones((ms) => ms.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  // Past dates (#155) — the rules live in lib/goalDates.mjs.
  const todayISO = localISODate(new Date());
  const savedDates = savedGoalDates(goal);
  const datesProblem = () => goalDatesProblem({ startDate, byDate, milestones, today: todayISO, saved: savedDates });
  const floor = (d, ...mins) => pickerMin(d, { today: todayISO, saved: savedDates }, ...mins);
  const addMilestone = () => setMilestones((ms) => [...ms, { byDate: '', targetValue: '' }]);
  const removeMilestone = (i) => setMilestones((ms) => ms.filter((_, j) => j !== i));

  async function save() {
    // Sections start closed, so a complaint about a field nobody can see is a
    // dead end — every validation failure opens the section that owns the field.
    const fail = (sec, msg) => { openOnly(sec); setErr(msg); };
    if (!name.trim()) { fail('basics', 'Give the goal a name.'); return; }
    if (isComp) {
      if (compMode === 'breakdown' && (!dashboardId || !tileId)) { fail('target', 'Pick the breakdown tile.'); return; }
      const named = parts.filter((p) => String(p.label).trim());
      if (named.length < 2) { fail('target', 'Add at least two slices to the split.'); return; }
      if (compMode === 'tiles' && named.some((p) => !(p.ref && p.ref.tileId))) { fail('target', 'Pick a tile for every slice.'); return; }
    } else {
      if (track === 'tile' && (!dashboardId || !tileId)) { fail('track', 'Pick the dashboard tile to track.'); return; }
      if (!target || Number.isNaN(Number(target))) { fail('target', 'Set a numeric target.'); return; }
      const dp = datesProblem();
      if (dp) { fail('dates', dp); return; }
    }
    setBusy(true); setErr('');
    const baseNum = baselineValue !== '' && !Number.isNaN(Number(baselineValue)) ? Number(baselineValue) : null;
    const tol = partTol !== '' && !Number.isNaN(Number(partTol)) ? Number(partTol) : 5;
    const body = {
      name: name.trim(),
      source: 'manual', // resolution is driven by the tile ref below, not this label
      metricRef: (!isComp && track === 'metric') ? (gMetricRef() || {}) : ((isComp && compMode === 'breakdown') || (!isComp && track === 'tile') ? { dashboardId, tileId } : {}),
      parts: isComp ? parts.filter((p) => String(p.label).trim()).map((p) => ({
        label: String(p.label).trim(), target: Number(p.target) || 0, tol,
        ...(p.focus ? { focus: true } : {}),
        ...(compMode === 'tiles' && p.ref && p.ref.tileId ? { ref: refWithNames(p.ref.dashboardId, p.ref.tileId) } : {}),
        ...(compMode === 'tiles' && p.lastRef && p.lastRef.tileId ? { lastRef: refWithNames(p.lastRef.dashboardId, p.lastRef.tileId) } : {}),
      })) : [],
      targetValue: isComp ? 0 : Number(target),
      targetMax: direction === 'range' && targetMax !== '' ? Number(targetMax) : null,
      unit, tag: tag.trim(), direction, display, byDate, startDate,
      scope: isPersonal ? 'personal' : 'event',
      isNorthStar: isPersonal ? false : northStar,
      visibility: isPersonal ? visibility : 'team',
      rollsUpTo: isPersonal ? rollsUpTo : '',
      // Baseline persists whenever there's a number — whether read from a past
      // event's tile, a picked tile (remembered + re-read live), or typed by hand.
      baselineEventId: baseNum != null && baselineMode === 'event' ? baselineSuiteId : '',
      baselineValue: baseNum,
      baselineSource: baseNum != null ? (baselineMode === 'tile' ? 'looker' : (baselineMode === 'event' && track === 'tile' ? 'looker' : 'manual')) : '',
      baselineRef: baselineMode === 'tile' && baselineDashboardId && baselineTileId ? { dashboardId: baselineDashboardId, tileId: baselineTileId } : null,
      milestones: milestones
        .map((m) => ({ byDate: m.byDate, targetValue: Number(m.targetValue), ...(m.lastValue != null && m.lastValue !== '' ? { lastValue: Number(m.lastValue) } : {}) }))
        .filter((m) => m.byDate && Number.isFinite(m.targetValue)),
      // Remember the linked checkpoint-curve tile so reopening restores it.
      curveRef: curveSeriesId ? { seriesId: curveSeriesId, cadence: curveCadence } : (curveDashboardId && curveTileId) ? { dashboardId: curveDashboardId, tileId: curveTileId, cadence: curveCadence, ...(compareKey ? { compareKey } : {}), ...(thisKey ? { thisKey } : {}) } : null,
    };
    try {
      let saved;
      if (editing) saved = (await api.updateGoal(goal.id, body)).goal;
      else saved = (await api.createGoal(activeSuite, body)).goal;
      // Manual goal with a starting value → record the first snapshot.
      if (track === 'manual' && current !== '' && !Number.isNaN(Number(current)) && saved?.id) {
        await api.goalSnapshot(saved.id, Number(current)).catch(() => {});
      }
      onSaved?.();
      onClose();
    } catch (e) { setErr(e.message || 'Could not save the goal.'); setBusy(false); }
  }

  async function del() {
    setBusy(true); setErr('');
    try { await api.deleteGoal(goal.id); onSaved?.(); onClose(); }
    catch (e) { setErr(e.message || 'Could not delete the goal.'); setBusy(false); }
  }

  return (
    <div style={overlay(isMobile)} onClick={onClose}>
      {/* The sheet is now a shell that does NOT scroll: the form column scrolls
          inside it so the preview can stay put. Desktop puts the preview in a
          column beside the form; a phone has no room for that, so it's pinned
          across the bottom instead. Either way it's visible the whole way down
          the form, which is the point — you're building a card you can't see. */}
      <div
        style={{
          ...sheet(isMobile),
          maxWidth: isMobile ? '100%' : (showPreview ? 820 : 460),
          padding: 0, overflow: 'hidden',
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div style={formCol(isMobile)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 20 }}>{isPersonal ? '🙋' : '🎯'}</span>
          <h2 style={{ fontSize: 17, fontWeight: 800, flex: 1 }}>{editing ? (isPersonal ? 'Edit personal goal' : 'Edit goal') : (isPersonal ? 'Set a personal goal' : 'Set a goal')}</h2>
          <button onClick={onClose} style={xBtn} aria-label="Close">✕</button>
        </div>

        {/* Two modes rather than a tick-box: which kind of setup you're in is a
            choice you make first, and naming BOTH sides says plainly that there
            is a simple path — a lone "Advanced options" checkbox only hints at
            it. The line underneath says what Advanced adds, so nobody has to
            switch to it to find out. A goal already using an advanced feature
            opens on Advanced. */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Seg active={!advanced} onClick={() => setMode(false)}>Basic</Seg>
            <Seg active={advanced} onClick={() => setMode(true)}>Advanced</Seg>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6, lineHeight: 1.45 }}>
            {advanced
              ? 'Build your own metric · suggest checkpoints from last time’s curve · mix/split goals.'
              : 'Track a number off a dashboard and set a target. Advanced adds your own metric, checkpoint suggestions and mix/split goals.'}
          </div>
        </div>

        {/* Start from a saved template (reuse a recurring goal's setup). */}
        {!editing && templates.length > 0 && (
          <Field label="Start from a template">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {templates.map((t) => (
                <span key={t.id} style={tmplChip}>
                  <button type="button" onClick={() => applyTemplate(t.payload)} style={tmplChipBtn} title={t.payload?.meta?.blurb || (t.global ? 'Global template (links your own tiles)' : 'Use this template')}>{t.payload?.meta?.emoji ? `${t.payload.meta.emoji} ` : t.global ? '🌐 ' : ''}{t.name}</button>
                  {(!t.global || isAdmin) && <button type="button" onClick={() => removeTemplate(t.id)} aria-label="Delete template" style={tmplChipX}>✕</button>}
                </span>
              ))}
            </div>
          </Field>
        )}

        <Section {...secProps('basics')} title="The basics" icon="notes">
        <Field label="What's the goal?">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sell-through, Bar revenue, Sponsorship secured" style={inp} autoFocus />
        </Field>

        <Field label="Category (optional)" hint="Group this goal by operational area — the Goals page shows one row per category. Pick one or create your own (shared with alerts).">
          <input value={tag} onChange={(e) => setTag(e.target.value)} list="goal-tag-presets" placeholder="e.g. Ticketing, Cashless, Access control" style={inp} />
          <datalist id="goal-tag-presets">
            {allTags.map((t) => <option key={t} value={t} />)}
          </datalist>
          {tagIsNew && (
            <button type="button" onClick={saveCategory} style={addCatBtn}>＋ Save “{tag.trim()}” as a category</button>
          )}
        </Field>

        {suites.length > 1 && (
          <Field label="For which event?" hint={editing ? 'A goal stays with its event.' : undefined}>
            <select value={activeSuite} onChange={(e) => setActiveSuite(e.target.value)} disabled={editing} style={inp}>
              {suites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
        )}
        </Section>

        {!isComp && (<Section {...secProps('track')} title="What to track" icon="dashboards">
        <Field label="How do you want to track it?" hint={track === 'tile' ? 'The goal reads the live number from your dashboard — it updates automatically.' : 'You\'ll type in the current value yourself — great for targets not yet on a dashboard.'}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Seg active={track === 'tile'} onClick={() => setTrack('tile')}>📊 From my dashboard</Seg>
            {advanced && <Seg active={track === 'metric'} onClick={() => setTrack('metric')}>🧩 Build a metric</Seg>}
            <Seg active={track === 'manual'} onClick={() => setTrack('manual')}>✍️ I'll enter it</Seg>
          </div>
        </Field>

        {track === 'metric' ? (
          <Field label="Build the metric" hint="Choose the data, filter it to the slice you care about (e.g. Ticket Type = VIP), then pick the measure. No tile needed — same machinery as metric alerts.">
            {/* Describe it instead of hunting for the field — fills the pickers below. */}
            <AskOwlMetric suiteId={activeSuite} entityId={entityId} inputStyle={inp} onFilled={fillFromOwl}
              onCompare={buildOwlCurve} compareBusy={!!owlCurve.busy} compareDone={owlCurve.done || ''} compareErr={owlCurve.err || ''} />
            <select value={gExKey} onChange={(e) => { setGExKey(e.target.value); setGMeasure(''); setGFilters([]); }} style={inp}>
              <option value="">{gExCat ? 'Choose the data…' : 'Loading…'}</option>
              {(gExCat?.explores || []).map((e2) => <option key={`${e2.model}::${e2.view}`} value={`${e2.model}::${e2.view}`}>{e2.label}</option>)}
            </select>
            {gExCat?.viaSuiteId && (gExCat.explores || []).length ? (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, lineHeight: 1.45 }}>
                These data sources come from “{(suites.find((x) => x.id === gExCat.viaSuiteId) || {}).name || 'another event'}” — this event has none of its own linked yet. The goal still tracks {(suites.find((x) => x.id === activeSuite) || {}).name || 'this event'}.
              </div>
            ) : null}
            {gExCat && !(gExCat.explores || []).length && (
              <div style={{ marginTop: 8, fontSize: 12, color: (gExCat.error || (gExCat.skipped || []).length) ? '#b45309' : 'var(--muted)', lineHeight: 1.4 }}>
                {gExCat.error
                  ? `Couldn't load the data sources for this event: ${gExCat.error}. Use “From my dashboard” for now — and send this message to Howler.`
                  : (gExCat.skipped || []).length
                    ? `Couldn't read ${gExCat.skipped.length} data source${gExCat.skipped.length === 1 ? '' : 's'} from Looker right now (${gExCat.skipped[0].error}). Try again in a minute, or use “From my dashboard” — and tell Howler if this keeps happening.`
                    : gExCat.dashboardCount === 0
                      ? `No dashboards are linked to this event yet, so there's nothing to build a metric from. Pick another event, or use “From my dashboard”.`
                      : gExCat.tileExplores === 0
                        ? `This event's ${gExCat.dashboardCount} dashboard${gExCat.dashboardCount === 1 ? '' : 's'} have no queryable tiles, so there's no data source to build from — use “From my dashboard”, and tell Howler this event needs its dashboards wired up.`
                        : gExCat.noMeasures
                          ? `Found ${gExCat.noMeasures} data source${gExCat.noMeasures === 1 ? '' : 's'} for this event, but Looker exposes no measure on ${gExCat.noMeasures === 1 ? 'it' : 'them'} — tell Howler; use “From my dashboard” meanwhile.`
                          : `No data sources available yet (these come from this event's dashboards). Use “From my dashboard” instead.`}
              </div>
            )}
            {gExplore && (<>
              {/* Event — pin the goal to ONE event so its number can never span others. */}
              {gEventField && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Event</div>
                  {Array.isArray(gValsOf(gEventField)) ? (
                    <select value={gEventValue} onChange={(e) => setGEventValue(e.target.value)} style={inp}>
                      <option value="">All events in scope</option>
                      {gValsOf(gEventField).map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  ) : <div style={{ fontSize: 12, color: 'var(--muted)' }}>Loading events…</div>}
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Pins this goal to one event, so its number can’t include other events. Defaults to {gSuiteName ? <b>{gSuiteName}</b> : 'this event'}.</div>
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                {gFilters.map((f, i) => {
                  if (f.field && f.field === gEventField) return null;
                  const vals = gValsOf(f.field);
                  const ready = Array.isArray(vals) && vals.length;
                  // A value carried over from another event (or a template) may not exist on
                  // the event now chosen — keep it selectable, but say so: it would read 0.
                  const orphan = ready && f.value && !vals.includes(f.value);
                  return (
                  <div key={i} style={{ marginBottom: 6 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                    <select value={f.field} onChange={(e) => { setGFilters((fs) => fs.map((x, j) => (j === i ? { field: e.target.value, value: '' } : x))); gLoadVals(e.target.value); }} style={{ ...inp, flex: 1, minWidth: 0 }}>
                      <option value="">Filter by…</option>
                      {gDims().filter((d) => d.name !== gEventField).map((d) => <option key={d.name} value={d.name}>{d.label}</option>)}
                    </select>
                    {f.field && (ready
                      ? <select value={f.value} onChange={(e) => setGFilters((fs) => fs.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} style={{ ...inp, flex: 1, minWidth: 0 }}>
                          <option value="">Choose a value…</option>
                          {orphan && <option value={f.value}>{f.value} (not on this event)</option>}
                          {vals.map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                      : <input value={f.value} onChange={(e) => setGFilters((fs) => fs.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} onFocus={() => gLoadVals(f.field)} placeholder={vals === 'loading' ? 'loading values…' : 'value (e.g. VIP)'} style={{ ...inp, flex: 1, minWidth: 0 }} />)}
                    <button type="button" onClick={() => setGFilters((fs) => fs.filter((_, j) => j !== i))} aria-label="Remove filter" style={{ ...inp, width: 40, flexShrink: 0, cursor: 'pointer', color: 'var(--muted)' }}>✕</button>
                    </div>
                    {orphan && (
                      <div style={{ fontSize: 11, color: '#b45309', marginTop: 3, lineHeight: 1.4 }}>
                        “{f.value}” isn’t a value on {gEventValue || 'this event'} — pick one from the list, or this goal reads 0.
                      </div>
                    )}
                  </div>
                  );
                })}
                <button type="button" onClick={() => setGFilters((fs) => [...fs, { field: '', value: '' }])} style={{ border: '1px dashed var(--hairline)', background: 'transparent', color: 'var(--brand)', borderRadius: 9, fontSize: 12, fontWeight: 700, padding: '8px 11px', cursor: 'pointer', width: '100%' }}>＋ Add a filter (e.g. Ticket Type, Category)</button>
              </div>
              <select value={gMeasure} onChange={(e) => setGMeasure(e.target.value)} style={{ ...inp, marginTop: 8 }}>
                <option value="">Choose the measure…</option>
                {(gExplore.measures || []).map((m) => <option key={m.name} value={m.name}>{m.label}</option>)}
              </select>
            </>)}
            {gMeasure && (
              <div style={{ marginTop: 9, padding: '8px 11px', background: 'rgba(128,128,128,0.07)', borderRadius: 9, fontSize: 13, display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ color: 'var(--muted)', fontWeight: 600 }}>This reads:</span>
                {gPreview?.loading
                  ? <span style={{ color: 'var(--muted)' }}>reading…</span>
                  : gPreview && gPreview.value != null
                    ? <b style={{ fontSize: 15 }}>{fmtNum(gPreview.value, unit)}</b>
                    : <span style={{ color: 'var(--muted)' }}>— couldn't read it right now</span>}
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>updates live</span>
              </div>
            )}
          </Field>
        ) : track === 'tile' ? (
          <Field label="Which number?" hint="Pick a single-value (KPI) tile you already look at — the goal tracks that live number.">
            <DashboardPicker preferSuiteId={activeSuite} value={dashboardId} onChange={(v) => { setDashboardId(v); setTileId(''); }} dashboards={dashFor('kpi', dashboardId)} isMobile={isMobile} placeholder={cat ? 'Choose a dashboard…' : 'Loading…'} />
            {dashNote('kpi')}
            {dashboardId && (tilesFor(dashboardId).length ? (<>
              <select value={tileId} onChange={(e) => setTileId(e.target.value)} style={{ ...inp, marginTop: 8 }}>
                <option value="">Choose a tile…</option>
                {tilesFor(dashboardId).map((t) => <option key={t.tileId} value={t.tileId}>{t.title}</option>)}
              </select>
              {/* Two ways to pick the same thing: name it from the list, or
                  recognise it from the dashboard laid out with live numbers.
                  Tile titles alone are often ambiguous ("Tickets sold" vs
                  "Tickets sold (paid)") — the values tell them apart. */}
              <button type="button" onClick={() => setBrowse((b2) => !b2)} style={curveToggle}>
                {browse ? 'Hide the dashboard' : '▦ Or pick it off the dashboard'}
              </button>
              {browse && (
                <TileBrowser
                  suiteId={activeSuite}
                  dashboardId={dashboardId}
                  tiles={tilesFor(dashboardId)}
                  value={tileId}
                  onPick={(id) => { setTileId(id); setBrowse(false); }}
                  unit={unit}
                  fmt={fmtNum}
                />
              )}
            </>) : (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>
                No single-value (KPI) tiles on this dashboard. Goals track one headline number — pick a dashboard with a KPI tile, or choose “✍️ I’ll enter it”.
              </div>
            ))}
            {tileId && (<>
              <div style={{ marginTop: 9, padding: '8px 11px', background: 'rgba(128,128,128,0.07)', borderRadius: 9, fontSize: 13, display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ color: 'var(--muted)', fontWeight: 600 }}>{preview?.source === 'curve-this-year' ? 'This goal will read:' : 'This tile reads:'}</span>
                {preview?.loading
                  ? <span style={{ color: 'var(--muted)' }}>reading…</span>
                  : preview && preview.value != null
                    ? <b style={{ fontSize: 15 }}>{fmtNum(preview.value, unit)}</b>
                    : <span style={{ color: 'var(--muted)' }}>— couldn't read it right now</span>}
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>updates live</span>
              </div>
              {/* A curve-linked goal takes its current number from the curve tile's
                  this-year column (that column also drives the pace line and the
                  forecast, so the two must be one measure). Say it here — and shout
                  when the two tiles disagree, which means they're counting different
                  things and the goal's %, pace and forecast follow the CURVE. */}
              {preview?.source === 'curve-this-year' && (
                <div style={{ marginTop: 5, fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45 }}>
                  Current comes from your <b>“compare to last time” curve</b> (its this-year column) — the same measure that drives pace and the forecast{preview.tileValue != null ? `, not the tile's own ${fmtNum(preview.tileValue, unit)}` : ''}.
                </div>
              )}
              {preview?.divergence && (
                <div style={{ marginTop: 5, fontSize: 11.5, color: '#b45309', lineHeight: 1.45 }}>
                  ⚠ Your KPI tile reads <b>{fmtNum(preview.divergence.tile, unit)}</b> but the curve reads <b>{fmtNum(preview.divergence.curve, unit)}</b> ({preview.divergence.pct > 0 ? '+' : ''}{preview.divergence.pct}%) — they’re measuring different things (e.g. one ticket type vs all tickets). Point the curve at the same measure, or the goal’s %, pace and forecast will be judged on the curve’s number.
                </div>
              )}
            </>)}
          </Field>
        ) : (
          <Field label="Current value (optional)" hint="You can update this any time; the goal tracks what you enter.">
            <input value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="e.g. 120000" inputMode="decimal" style={inp} />
          </Field>
        )}

        </Section>)}

        {!isComp && (<Section {...secProps('baseline')} title="Compare to last time" icon="trend">
        <Field label="Compare to last time (optional)" hint="Start from last time — the goal shows “vs last time” and can suggest a target.">
          <select
            value={baselineMode === 'event' ? baselineSuiteId : (baselineMode === 'tile' ? '__tile__' : '')}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') { setBaselineMode('manual'); setBaselineSuiteId(''); setBaselineValue(''); }
              else if (v === '__tile__') { setBaselineMode('tile'); setBaselineSuiteId(''); setBaselineValue(''); }
              else { setBaselineMode('event'); setBaselineSuiteId(v); }
            }}
            style={inp}
          >
            <option value="">Enter it manually…</option>
            <option value="__tile__">📊 From a dashboard tile…</option>
            {pastSuites.map((s) => <option key={s.id} value={s.id}>{s.name}{s.id === activeSuite ? ' (this event)' : ''}</option>)}
          </select>
          {/* Pick any dashboard + tile for last time's number (e.g. a last-year KPI). */}
          {baselineMode === 'tile' && (
            <>
              <DashboardPicker preferSuiteId={activeSuite} value={baselineDashboardId} onChange={(v) => { setBaselineDashboardId(v); setBaselineTileId(''); setBaselineValue(''); }} dashboards={dashFor('kpi', baselineDashboardId)} isMobile={isMobile} placeholder={cat ? 'Choose a dashboard…' : 'Loading…'} style={{ marginTop: 8 }} />
              {dashNote('kpi')}
              {baselineDashboardId && (
                <select value={baselineTileId} onChange={(e) => setBaselineTileId(e.target.value)} style={{ ...inp, marginTop: 8 }}>
                  <option value="">Choose a tile…</option>
                  {tilesFor(baselineDashboardId).map((t) => <option key={t.tileId} value={t.tileId}>{t.title}</option>)}
                </select>
              )}
            </>
          )}
          {autoMode && baselineLoading && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--muted)' }}>reading last time…</div>}
          {autoMode && !baselineLoading && !baseFinite && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>Couldn’t read that tile — pick another or enter it below.</div>}
          {(baselineMode === 'manual' || (autoMode && !baselineLoading && !baseFinite)) && (
            <input value={baselineValue} onChange={(e) => setBaselineValue(e.target.value)} placeholder="Last time’s value" inputMode="decimal" style={{ ...inp, marginTop: 8 }} />
          )}
          {baseFinite && (
            <div style={{ marginTop: 9 }}>
              <div style={{ fontSize: 13 }}><span style={{ color: 'var(--muted)', fontWeight: 600 }}>Last time:</span> <b>{fmtNum(Number(baselineValue), unit)}</b></div>
              <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11.5, color: 'var(--muted)', alignSelf: 'center' }}>Set target:</span>
                {[['Match', 1], ['+10%', 1.1], ['+15%', 1.15], ['+20%', 1.2]].map(([lbl, f]) => (
                  <button key={lbl} type="button" onClick={() => setTarget(String(Math.round(Number(baselineValue) * f)))} style={suggestBtn}>{lbl}</button>
                ))}
              </div>
            </div>
          )}
        </Field>
        </Section>)}

        <Section {...secProps('target')} title="The target" icon="goals">
        <Field label="Goal type" hint={advanced ? '“Healthy range” flags going too far over; “Mix / split” tracks shares of a whole.' : '“Healthy range” flags going too far over.'}>
          <select value={direction} onChange={(e) => setDirection(e.target.value)} style={inp}>
            <option value="at_least">Hit a target — reach the number or beat it ↑</option>
            <option value="at_most">Stay under a cap — keep the number below it ↓</option>
            <option value="range">Healthy range — stay within a band (flag over) ↕</option>
            {(advanced || isComp) && <option value="composition">Mix / split — shares of a 100% whole (New/Returning, age…) ◑</option>}
          </select>
        </Field>

        {!isComp && (
        <div style={{ display: 'flex', gap: 10 }}>
          <Field label={direction === 'range' ? 'Range — low' : 'Target'} style={{ flex: 1 }}>
            <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder={direction === 'range' ? 'e.g. 30' : 'e.g. 25000'} inputMode="decimal" style={inp} />
          </Field>
          {direction === 'range' && (
            <Field label="Range — high" style={{ flex: 1 }}>
              <input value={targetMax} onChange={(e) => setTargetMax(e.target.value)} placeholder="e.g. 38" inputMode="decimal" style={inp} />
            </Field>
          )}
          <Field label="Unit" style={{ width: 110 }}>
            <select value={unit} onChange={(e) => setUnit(e.target.value)} style={inp}>
              {[...new Set(['tickets', 'ZAR', '%', 'sessions', 'users', 'views', 'conversions', 'orders', 'count', unit].filter(Boolean))].map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
        </div>
        )}

        {/* Composition: shares from ONE breakdown tile, or a tile PER slice. */}
        {isComp && (
          <div style={{ border: '1px solid var(--hairline)', borderRadius: 12, padding: 12, marginBottom: 12 }}>
            <Field label="Where do the slices come from?">
              <div style={{ display: 'flex', gap: 8 }}>
                <Seg active={compMode === 'breakdown'} onClick={() => setCompMode('breakdown')}>📊 One breakdown tile</Seg>
                <Seg active={compMode === 'tiles'} onClick={() => setCompMode('tiles')}>🔢 A tile per slice</Seg>
              </div>
            </Field>

            <Field label="Unit" hint="What each slice's number is measured in — used for the live readouts. The split itself is always shown as %." style={{ width: 140 }}>
              <select value={unit} onChange={(e) => setUnit(e.target.value)} style={inp}>
                {[...new Set(['tickets', 'ZAR', '%', 'sessions', 'users', 'views', 'conversions', 'orders', 'count', unit].filter(Boolean))].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>

            {compMode === 'breakdown' ? (
              <>
                <Field label="Breakdown tile" hint="A chart/table split by category (e.g. customers by type, audience by age).">
                  <DashboardPicker preferSuiteId={activeSuite} value={dashboardId} onChange={(v) => { setDashboardId(v); setTileId(''); setParts([]); }} dashboards={dashFor('series', dashboardId)} isMobile={isMobile} placeholder={cat ? 'Choose a dashboard…' : 'Loading…'} />
                  {dashNote('series')}
                  {dashboardId && (
                    <select value={tileId} onChange={(e) => { setTileId(e.target.value); setParts([]); }} style={{ ...inp, marginTop: 8 }}>
                      <option value="">Choose a tile…</option>
                      {seriesTilesFor(dashboardId).map((t) => <option key={t.tileId} value={t.tileId}>{t.title}</option>)}
                    </select>
                  )}
                </Field>
                {tileId && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 8px' }}>
                      <span style={{ fontSize: 12, fontWeight: 700, flex: 1 }}>Target split{parts.length ? ` — sums to ${parts.reduce((s, p) => s + (Number(p.target) || 0), 0)}%` : ''}</span>
                      <button type="button" onClick={loadSegments} style={{ ...addMsBtn }}>{partsLoading ? '…' : '↻ Reload segments'}</button>
                    </div>
                    {parts.map((p, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <button type="button" onClick={() => setParts((ps) => ps.map((x, j) => ({ ...x, focus: j === i ? !x.focus : x.focus })))} title="Focus slice (Owl targets this to grow it)" style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, opacity: p.focus ? 1 : 0.35 }}>🎯</button>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label}</span>
                        <input value={p.target} onChange={(e) => setParts((ps) => ps.map((x, j) => j === i ? { ...x, target: e.target.value } : x))} inputMode="decimal" style={{ ...inp, width: 64, textAlign: 'right' }} />
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>%</span>
                      </div>
                    ))}
                  </>
                )}
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, flex: 1 }}>Slices{parts.length ? ` — targets sum to ${parts.reduce((s, p) => s + (Number(p.target) || 0), 0)}%` : ''}</span>
                  <button type="button" onClick={() => setParts((ps) => [...ps, { label: '', target: 0, ref: { dashboardId: '', tileId: '' } }])} style={{ ...addMsBtn }}>＋ Add slice</button>
                </div>
                {parts.map((p, i) => {
                  const setPart = (patch) => setParts((ps) => ps.map((x, j) => j === i ? { ...x, ...patch } : x));
                  const ref = p.ref || {};
                  const lastRef = p.lastRef || {};
                  return (
                    <div key={i} style={{ border: '1px solid var(--hairline)', borderRadius: 9, padding: 8, marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <button type="button" onClick={() => setPart({ focus: !p.focus })} title="Focus slice (the one to grow)" style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, opacity: p.focus ? 1 : 0.35 }}>🎯</button>
                        <input value={p.label} onChange={(e) => setPart({ label: e.target.value })} placeholder="Slice name (e.g. New)" style={{ ...inp, flex: 1 }} />
                        <input value={p.target} onChange={(e) => setPart({ target: e.target.value })} inputMode="decimal" style={{ ...inp, width: 58, textAlign: 'right' }} />
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>%</span>
                        <button type="button" onClick={() => setParts((ps) => ps.filter((_, j) => j !== i))} aria-label="Remove slice" style={{ border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}>✕</button>
                      </div>
                      <DashboardPicker preferSuiteId={activeSuite} value={ref.dashboardId || ''} onChange={(v) => setPart({ ref: { dashboardId: v, tileId: '' } })} dashboards={dashFor('kpi', ref.dashboardId)} isMobile={isMobile} placeholder={cat ? 'Choose a dashboard…' : 'Loading…'} />
                      {dashNote('kpi')}
                      {ref.dashboardId && (
                        <select value={ref.tileId || ''} onChange={(e) => setPart({ ref: { ...ref, tileId: e.target.value } })} style={{ ...inp, marginTop: 6 }}>
                          <option value="">Choose a tile…</option>
                          {tilesFor(ref.dashboardId).map((t) => <option key={t.tileId} value={t.tileId}>{t.title}</option>)}
                        </select>
                      )}
                      <TilePeek suiteId={activeSuite} dashboardId={ref.dashboardId} tileId={ref.tileId} unit={unit} label="this slice reads" />
                      {/* Optional last-year tile for this slice — shows movement (▲/▼ pp vs last year). */}
                      {ref.tileId && (
                        <details style={{ marginTop: 8 }}>
                          <summary style={{ fontSize: 11.5, color: 'var(--brand)', cursor: 'pointer', fontWeight: 700 }}>Compare to last year (optional)</summary>
                          <DashboardPicker preferSuiteId={activeSuite} value={lastRef.dashboardId || ''} onChange={(v) => setPart({ lastRef: { dashboardId: v, tileId: '' } })} dashboards={dashFor('kpi', lastRef.dashboardId)} isMobile={isMobile} placeholder={cat ? 'Choose a dashboard…' : 'Loading…'} style={{ marginTop: 6 }} />
                          {lastRef.dashboardId && (
                            <select value={lastRef.tileId || ''} onChange={(e) => setPart({ lastRef: { ...lastRef, tileId: e.target.value } })} style={{ ...inp, marginTop: 6 }}>
                              <option value="">Choose last-year tile…</option>
                              {tilesFor(lastRef.dashboardId).map((t) => <option key={t.tileId} value={t.tileId}>{t.title}</option>)}
                            </select>
                          )}
                          <TilePeek suiteId={activeSuite} dashboardId={lastRef.dashboardId} tileId={lastRef.tileId} unit={unit} label="last year reads" />
                        </details>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {parts.length > 0 && (
              <Field label="Tolerance (± percentage points)" hint="How far a slice can drift before it's flagged." style={{ marginTop: 6 }}>
                <input value={partTol} onChange={(e) => setPartTol(e.target.value)} inputMode="decimal" style={{ ...inp, width: 90 }} />
              </Field>
            )}

            <Field label="Show it as" style={{ marginBottom: 0, marginTop: 6 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <Seg active={display === 'bar'} onClick={() => setDisplay('bar')}>▭ Stacked bar</Seg>
                <Seg active={display === 'ring'} onClick={() => setDisplay('ring')}>◯ Donut</Seg>
                <Seg active={display === 'dial'} onClick={() => setDisplay('dial')}>◔ Dial</Seg>
              </div>
            </Field>
          </div>
        )}

        </Section>

        {!isComp && (<Section {...secProps('dates')} title="Dates & checkpoints" icon="digests">
        <div style={{ display: 'flex', gap: 10 }}>
          <Field label="Track from" hint="When selling started — pace runs from here. Defaults to today." style={{ flex: 1 }}>
            <input type="date" value={startDate} max={byDate || undefined} onChange={(e) => setStartDate(e.target.value)} style={inp} />
          </Field>
          <Field label="By (deadline)" hint="Defaults to event day" style={{ flex: 1 }}>
            <input type="date" value={byDate} min={floor(byDate, startDate)} onChange={(e) => setByDate(e.target.value)} style={inp} />
          </Field>
        </div>

        <Field label="Checkpoints (optional)" hint="Weekly or monthly targets on the way — pace is measured against the nearest one, so a back-loaded goal isn’t flagged “behind” too early.">
          {milestones.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <input type="date" value={m.byDate || ''} min={floor(m.byDate, startDate)} max={byDate || undefined} onChange={(e) => updateMilestone(i, { byDate: e.target.value })} style={{ ...inp, flex: 1 }} />
              <input value={m.targetValue ?? ''} onChange={(e) => updateMilestone(i, { targetValue: e.target.value })} placeholder="target" inputMode="decimal" style={{ ...inp, width: 104 }} />
              <button type="button" onClick={() => removeMilestone(i)} aria-label="Remove checkpoint" style={msX}>✕</button>
            </div>
          ))}
          <button type="button" onClick={addMilestone} style={addMsBtn}>＋ Add a checkpoint</button>

          {/* Suggest checkpoints from last time's curve — a power tool: it needs a
              time-series tile and a comparable past event, so it only appears
              once you've asked for advanced options. */}
          {advanced && (<>
          <button type="button" onClick={() => setCurveOpen((o) => !o)} style={curveToggle}>
            📈 {curveOpen ? 'Hide suggestions' : 'Suggest from last time’s curve'}
          </button>
          {curveOpen && (
            <div style={curveBox}>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 8, lineHeight: 1.45 }}>
                Link a tile that shows the value over time (e.g. sales by date). We read its shape under {baselineSuiteId ? 'the event chosen above' : 'this event'} and suggest checkpoints scaled to your target.
                {(seriesList || []).length === 0 && ' No history in Pulse yet? Upload last year as a spreadsheet instead.'}
              </div>
              {/* Where last time's shape comes from: a dashboard tile, or an uploaded
                  spreadsheet for an event with no history in Looker. */}
              {seriesList !== null && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <Seg active={!curveSeriesId} onClick={() => setCurveSeriesId('')}>From a dashboard</Seg>
                  <Seg active={!!curveSeriesId} onClick={() => { if (!curveSeriesId && (seriesList || []).length) setCurveSeriesId(seriesList[0].id); else if (!curveSeriesId) fileRef.current?.click(); }}>From a spreadsheet</Seg>
                </div>
              )}
              {curveSeriesId ? (<>
                {(seriesList || []).length > 0 && (
                  <select value={curveSeriesId} onChange={(e) => setCurveSeriesId(e.target.value)} style={inp}>
                    {(seriesList || []).map((s) => <option key={s.id} value={s.id}>{s.name} · {s.points} points{s.from != null ? ` (${s.from} → ${s.to})` : ''}</option>)}
                  </select>
                )}
                {/* How the numbers were read. A rate/level is used as-is; only
                    per-period quantities are summed into a running total. */}
                {(() => {
                  const cur = (seriesList || []).find((s) => s.id === curveSeriesId);
                  if (!cur?.kind) return null;
                  return (
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, lineHeight: 1.45 }}>
                      Read as <b style={{ color: 'var(--text)' }}>{cur.kind}</b>
                      {cur.kind === 'rate' || cur.kind === 'level'
                        ? ' — each row is the value at that point, not added up.'
                        : cur.kind.startsWith('per-period')
                          ? ' — the rows were added into a running total.'
                          : ' — the rows already climb, so they were used as they are.'}
                      {' '}Ends at <b style={{ color: 'var(--text)' }}>{cur.total}</b>.
                    </div>
                  );
                })()}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={seriesBusy} style={{ ...btnGhost, flex: 'none', padding: '7px 12px', fontSize: 12 }}>{seriesBusy ? 'Reading…' : '↑ Upload another'}</button>
                  {curveSeriesId && (seriesList || []).some((s) => s.id === curveSeriesId) && (
                    <button type="button" style={{ ...btnGhost, flex: 'none', padding: '7px 12px', fontSize: 12, color: 'var(--error,#dc2626)' }}
                      onClick={() => { const id = curveSeriesId; if (!window.confirm('Delete this uploaded curve? Goals using it fall back to a straight pace line.')) return; api.goalSeriesDelete(entityId, id).then(() => { setSeriesList((l) => (l || []).filter((s) => s.id !== id)); setCurveSeriesId(''); }).catch((e) => setSeriesErr(e.message)); }}>Delete</button>
                  )}
                </div>
              </>) : (<>
                <DashboardPicker preferSuiteId={activeSuite} value={curveDashboardId} onChange={(v) => { setCurveDashboardId(v); setCurveTileId(''); }} dashboards={dashFor('series', curveDashboardId)} isMobile={isMobile} placeholder={cat ? 'Choose a dashboard…' : 'Loading…'} />
                {dashNote('series')}
                {curveDashboardId && (
                  <select value={curveTileId} onChange={(e) => setCurveTileId(e.target.value)} style={{ ...inp, marginTop: 8 }}>
                    <option value="">Choose a time-series tile…</option>
                    {seriesTilesFor(curveDashboardId).map((t) => <option key={t.tileId} value={t.tileId}>{t.title}</option>)}
                  </select>
                )}
              </>)}
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv" onChange={(e) => uploadCurve(e.target.files?.[0])} style={{ display: 'none' }} />
              {seriesErr && <div style={{ fontSize: 11.5, color: 'var(--error,#dc2626)', marginTop: 6 }}>{seriesErr}</div>}
              {curveSeriesId === '' && seriesList !== null && (seriesList || []).length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, lineHeight: 1.45 }}>
                  Spreadsheet format: two columns — the point (a date, or days-before-event) and the value. Running totals or per-period both work.
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Seg active={curveCadence === 'weekly'} onClick={() => setCurveCadence('weekly')}>Weekly</Seg>
                <Seg active={curveCadence === 'monthly'} onClick={() => setCurveCadence('monthly')}>Monthly</Seg>
              </div>
              {/* This event (actual) — which pivot column IS the current event. Auto follows
                  the suite's Current Event lock; the override fixes a wrong auto-pick (e.g.
                  a year-keyed pivot, or a smaller current edition mistaken for "last time"). */}
              {curveAllKeys.length > 1 && (
                <label style={{ display: 'block', marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
                  This event (actual)
                  <select value={thisKey} onChange={(e) => { setThisKey(e.target.value); if (e.target.value && e.target.value === compareKey) setCompareKey(''); }} style={{ ...inp, marginTop: 4 }}>
                    <option value="">Auto{detectedThisKey ? ` (${detectedThisKey})` : ''}</option>
                    {curveAllKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </label>
              )}
              {/* Compare-against (past event / year) — only when the tile carries more than one prior period. */}
              {curveYears.length > 1 && (
                <label style={{ display: 'block', marginTop: 8, fontSize: 12, color: 'var(--muted)' }}>
                  Compare against (past event)
                  <select value={compareKey} onChange={(e) => setCompareKey(e.target.value)} style={{ ...inp, marginTop: 4 }}>
                    <option value="">Last time ({curveYears[0]})</option>
                    {curveYears.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </label>
              )}
              {curveSeries?.loading && <div style={hintRow}>reading last time…</div>}
              {Array.isArray(curveSeries) && curveSeries.length === 0 && curveTileId && <div style={hintRow}>Couldn’t read a time series from that tile — pick a chart with a date dimension.</div>}
              {Array.isArray(curveSeries) && curveSeries.length >= 2 && (() => {
                const sugg = buildSuggestions();
                return (
                  <div style={{ marginTop: 10 }}>
                    {/* Preview — last time's shape, so you can see we picked it up. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <CurveSparkline series={curveSeries} />
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>last time’s shape · {curveSeries.length} points read</span>
                    </div>
                    {sugg.length ? (
                      <>
                        {sugg.map((s, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '3px 0', fontSize: 12.5 }}>
                            <span style={{ flex: 1, color: 'var(--muted)' }}>{fmtShort(s.byDate)}</span>
                            <span style={{ color: 'var(--muted)', fontSize: 11 }}>last time {fmtNum(s.lastValue, unit)}</span>
                            <span style={{ fontWeight: 700 }}>{fmtNum(s.targetValue, unit)}</span>
                          </div>
                        ))}
                        <button type="button" onClick={() => setMilestones(sugg.map((s) => ({ byDate: s.byDate, targetValue: String(s.targetValue), lastValue: s.lastValue })))} style={applyBtn}>
                          Use these {sugg.length} checkpoints
                        </button>
                      </>
                    ) : (
                      <div style={hintRow}>{suggestReason()}</div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          </>)}
        </Field>

        </Section>)}

        {!isComp && (<Section {...secProps('look')} title="How it looks" icon="eye">
        <Field label="Show it as">
          <div style={{ display: 'flex', gap: 8 }}>
            <Seg active={display === 'bar'} onClick={() => setDisplay('bar')}>▭ Bar</Seg>
            <Seg active={display === 'ring'} onClick={() => setDisplay('ring')}>◯ Circle</Seg>
            <Seg active={display === 'dial'} onClick={() => setDisplay('dial')}>◔ Dial</Seg>
          </div>
        </Field>
        </Section>)}

        <Section {...secProps('who')} title={isPersonal ? 'Who can see it' : 'Priority'} icon={isPersonal ? 'user' : 'pin'}>
        {isPersonal ? (
          <>
            <Field label="Who can see this?" hint="Team-visible goals show on the event so everyone sees who’s driving what. Private goals are just you (and Howler admins).">
              <div style={{ display: 'flex', gap: 8 }}>
                <Seg active={visibility === 'team'} onClick={() => setVisibility('team')}>👥 My team</Seg>
                <Seg active={visibility === 'private'} onClick={() => setVisibility('private')}>🔒 Just me</Seg>
              </div>
            </Field>
            {eventGoals.length > 0 && (
              <Field label="Contributes to (optional)" hint="Link this to an event goal it helps reach — it’ll show as a contributor on that goal.">
                <select value={rollsUpTo} onChange={(e) => setRollsUpTo(e.target.value)} style={inp}>
                  <option value="">Not linked</option>
                  {eventGoals.map((eg) => <option key={eg.id} value={eg.id}>{eg.name}</option>)}
                </select>
              </Field>
            )}
          </>
        ) : (
          <label style={northRow}>
            <input type="checkbox" checked={northStar} onChange={(e) => setNorthStar(e.target.checked)} />
            <span style={{ fontWeight: 700 }}>⭐ Make this the North Star</span>
            <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>the one headline goal for this event</span>
          </label>
        )}
        </Section>

        {err && <div style={{ color: 'var(--error, #dc2626)', fontSize: 12.5, marginTop: 10 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center' }}>
          {editing && (confirmDel ? (
            <button onClick={del} disabled={busy} style={btnDanger} title="Confirm delete">Delete goal</button>
          ) : (
            <button onClick={() => setConfirmDel(true)} style={btnDelGhost} title="Delete this goal" aria-label="Delete goal">🗑</button>
          ))}
          {!isPersonal && name.trim() && (
            <button onClick={saveAsTemplate} disabled={tmplBusy} style={btnGhost} title="Save this setup as a reusable template">
              {tmplBusy ? 'Saving…' : '📑 Save as template'}
            </button>
          )}
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={save} disabled={busy} style={btnPrimary}>{busy ? 'Saving…' : (editing ? 'Save goal' : 'Set goal')}</button>
        </div>
        </div>

        {showPreview && (isMobile ? (
          // Pinned across the bottom, above the home indicator. flexShrink:0 so a
          // long form can never squeeze it out.
          <div style={previewBarMobile}>
            <GoalCard goal={previewGoal} grid />
          </div>
        ) : (
          <aside style={previewColDesktop}>
            <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', marginBottom: 8 }}>Preview</div>
            <GoalCard goal={previewGoal} grid />
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10, lineHeight: 1.45 }}>
              This is the card your goal becomes — it updates as you fill the form in.
            </div>
          </aside>
        ))}
      </div>
    </div>
  );
}

const tmplChip = { display: 'inline-flex', alignItems: 'center', gap: 2, border: '1px solid var(--hairline)', borderRadius: 980, background: 'var(--card)', overflow: 'hidden' };
const tmplChipBtn = { border: 'none', background: 'transparent', color: 'var(--brand)', fontWeight: 700, fontSize: 12, cursor: 'pointer', padding: '6px 4px 6px 11px', fontFamily: 'inherit' };
const tmplChipX = { border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 11, padding: '6px 9px 6px 4px', lineHeight: 1 };

// A tap-to-open group of fields. Setting a goal can involve fifteen-plus inputs,
// and shown flat on a phone that's a long scroll of things you mostly don't need
// to touch. Collapsed, each section is one row: what it's for, and a recap of
// what's currently set — so the whole goal fits on one screen and you open only
// the part you're changing. The header is a full-width 44px tap target.
function Section({ id, title, icon, summary, open, onToggle, children }) {
  return (
    <div style={{ border: '1px solid var(--hairline)', borderRadius: 12, marginBottom: 8, overflow: 'hidden', background: 'var(--card)' }}>
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 44, padding: '10px 12px', border: 'none', background: 'transparent', color: 'var(--text)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
      >
        <Icon name={icon} size={15} style={{ flexShrink: 0, color: 'var(--muted)' }} />
        <span style={{ fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{title}</span>
        {/* The recap only earns its space while the section is shut. */}
        <span style={{ flex: 1, minWidth: 0, textAlign: 'right', fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{open ? '' : summary}</span>
        <span style={{ fontSize: 10, color: 'var(--muted)', flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
      </button>
      {/* Unmounted when shut, so a closed section costs nothing to render. */}
      {open && <div style={{ padding: '2px 12px 0' }}>{children}</div>}
    </div>
  );
}

function Field({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 12, ...style }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 5 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, lineHeight: 1.4 }}>{hint}</div>}
    </div>
  );
}
function Seg({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      flex: 1, padding: '9px 8px', borderRadius: 10, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
      border: `1.5px solid ${active ? 'var(--brand)' : 'var(--hairline)'}`,
      background: active ? 'rgba(var(--brand-rgb,10,132,255),0.10)' : 'var(--card)',
      color: active ? 'var(--brand)' : 'var(--text)',
    }}>{children}</button>
  );
}

function fmtNum(v, unit) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  const s = Math.abs(n) >= 1000 ? n.toLocaleString('en-ZA') : String(n);
  if (unit === 'ZAR') return `${currencySymbol(brandCurrency())}${s}`; // money unit; symbol follows the client's reporting currency
  if (unit === '%') return `${s}%`;
  return unit && unit !== 'count' ? `${s} ${unit}` : s;
}

// Live readout of a chosen tile's value (mix/split slices) so you can see the actual
// number you're splitting on as you pick. `label` distinguishes this-year vs last-year.
function TilePeek({ suiteId, dashboardId, tileId, unit, label = 'reads' }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (!suiteId || !dashboardId || !tileId) { setSt(null); return undefined; }
    let alive = true; setSt({ loading: true });
    api.goalTileValue(suiteId, dashboardId, tileId)
      .then((r) => { if (alive) setSt({ value: r.value }); })
      .catch(() => { if (alive) setSt({ value: null }); });
    return () => { alive = false; };
  }, [suiteId, dashboardId, tileId]);
  if (!tileId || !st) return null;
  return (
    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span>{label}:</span>
      {st.loading ? <span>reading…</span> : st.value != null ? <b style={{ color: 'var(--text)' }}>{fmtNum(st.value, unit)}</b> : <span>— couldn't read it</span>}
    </div>
  );
}

function fmtShort(s) { const d = new Date(s); return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }); }

// Tiny cumulative-shape preview of last time's curve, so you can see it was read.
function CurveSparkline({ series, w = 60, h = 22 }) {
  const vals = (series || []).map((p) => Number(p.v)).filter((v) => Number.isFinite(v));
  if (vals.length < 2) return null;
  const nonDec = vals.every((v, i) => i === 0 || v >= vals[i - 1] - 1e-9);
  let run = 0; const cum = vals.map((v) => { run = nonDec ? v : run + v; return run; });
  const max = cum[cum.length - 1] || 1;
  const pts = cum.map((c, i) => `${((i / (cum.length - 1)) * w).toFixed(1)},${(h - (c / max) * h).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ flexShrink: 0 }} aria-hidden="true">
      <polyline points={pts} fill="none" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Mobile = a FULL-SCREEN sheet (no floating dialog to fight with): the sheet is
// the only scroll container, sized with dvh so iOS Safari's collapsing URL bar
// can't hide the save row, and overscroll is contained so hitting the top/bottom
// never hands the swipe to the page behind.
const overlay = (isMobile) => ({
  position: 'fixed', inset: 0, background: isMobile ? 'var(--bg, #fff)' : 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center',
  padding: isMobile ? 0 : 16, zIndex: 100, overscrollBehavior: 'contain',
});
// The scrolling half. Carries the padding the sheet used to own, since the sheet
// itself must not scroll for the preview to stay still.
const formCol = (isMobile) => ({
  flex: 1, minWidth: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
  padding: isMobile ? '18px 16px 18px' : '18px 18px 20px',
});
// Desktop: a fixed column beside the form. It doesn't scroll, so it needs no
// position:sticky — it simply never moves.
const previewColDesktop = {
  width: 268, flexShrink: 0, borderLeft: '1px solid var(--hairline)', padding: '18px 18px 20px',
  background: 'rgba(128,128,128,0.04)', overflowY: 'auto',
};
// Phone: pinned across the bottom, clear of the home indicator.
const previewBarMobile = {
  flexShrink: 0, borderTop: '1px solid var(--hairline)', background: 'var(--card)',
  padding: '10px 16px calc(10px + env(safe-area-inset-bottom))',
  boxShadow: '0 -6px 18px rgba(0,0,0,0.10)',
};
const sheet = (isMobile) => ({
  width: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
  background: 'var(--card)', color: 'var(--text)',
  ...(isMobile
    ? { height: '100dvh', maxHeight: '100dvh', borderRadius: 0, border: 'none', padding: '18px 16px calc(24px + env(safe-area-inset-bottom))' }
    : { maxHeight: 'min(90vh, calc(100dvh - 32px))', borderRadius: 16, border: '1px solid var(--border)', padding: '18px 18px 20px', boxShadow: 'var(--shadow-lg, 0 12px 40px rgba(0,0,0,0.28))' }),
});
const inp = { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: '1.5px solid var(--hairline)', borderRadius: 9, fontSize: 14, outline: 'none', background: 'var(--card)', color: 'var(--text)', fontFamily: 'inherit' };
const xBtn = { border: 'none', background: 'rgba(128,128,128,0.12)', color: 'var(--muted-2)', borderRadius: 980, width: 28, height: 28, fontSize: 13, cursor: 'pointer' };
const linkBtn = { border: 'none', background: 'transparent', color: 'var(--brand)', fontWeight: 700, fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: 'inherit', textDecoration: 'underline' };
const northRow = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6, padding: '10px 12px', border: '1px solid var(--hairline)', borderRadius: 10, cursor: 'pointer', fontSize: 13 };
const suggestBtn = { border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--brand)', borderRadius: 980, fontSize: 11.5, fontWeight: 700, padding: '3px 10px', cursor: 'pointer' };
const msX = { border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--muted)', borderRadius: 9, fontSize: 13, cursor: 'pointer', flexShrink: 0, width: 38 };
const addMsBtn = { border: '1px dashed var(--hairline)', background: 'transparent', color: 'var(--brand)', borderRadius: 9, fontSize: 12.5, fontWeight: 700, padding: '7px 11px', cursor: 'pointer', width: '100%' };
const addCatBtn = { marginTop: 7, border: '1px dashed var(--hairline)', background: 'transparent', color: 'var(--brand)', borderRadius: 9, fontSize: 12, fontWeight: 700, padding: '6px 11px', cursor: 'pointer' };
const curveToggle = { border: 'none', background: 'transparent', color: 'var(--brand)', fontSize: 12, fontWeight: 700, padding: '8px 2px 2px', cursor: 'pointer', fontFamily: 'inherit' };
const curveBox = { marginTop: 4, padding: '11px 12px', background: 'rgba(128,128,128,0.06)', border: '1px solid var(--hairline)', borderRadius: 10 };
const hintRow = { marginTop: 8, fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 };
const applyBtn = { marginTop: 9, width: '100%', border: 'none', background: 'var(--brand)', color: '#fff', borderRadius: 9, fontSize: 12.5, fontWeight: 700, padding: '8px 11px', cursor: 'pointer' };
const btnGhost = { flex: '0 0 auto', padding: '10px 16px', borderRadius: 10, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' };
const btnDanger = { flex: '0 0 auto', padding: '10px 14px', borderRadius: 10, border: 'none', background: 'var(--error, #dc2626)', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' };
const btnDelGhost = { flex: '0 0 auto', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--error, #dc2626)', fontSize: 15, cursor: 'pointer' };
const btnPrimary = { flex: 1, padding: '10px 16px', borderRadius: 10, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' };
